import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { toast } from "sonner";
import { isTaskBlocked, type Column, type Project, type Task, type User } from "@fjord/shared";
import { useTasks, useProjects, useUsers } from "../lib/queries.js";
import { useActiveSpace } from "../lib/SpaceContext.js";
import { useMoveTask } from "../lib/mutations.js";
import { useIsMobile } from "../lib/useIsMobile.js";
import { FilterBar } from "./FilterBar.js";
import { useFilterContext } from "../lib/FilterContext.js";
import { applyTaskFilters, collectTags } from "../lib/taskFilters.js";
import { createUserLookup, formatAssigneeLabel } from "../lib/userLabels.js";
import { computeInsertPosition } from "../lib/dnd.js";

function DragGripIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
      <circle cx="2" cy="3" r="1.25" />
      <circle cx="8" cy="3" r="1.25" />
      <circle cx="2" cy="8" r="1.25" />
      <circle cx="8" cy="8" r="1.25" />
      <circle cx="2" cy="13" r="1.25" />
      <circle cx="8" cy="13" r="1.25" />
    </svg>
  );
}

type PromoteHandler = (task: Task, target: Column) => void;

interface RowProps {
  task: Task;
  project: Project | undefined;
  assigneeLabel: string;
  showProject: boolean;
  isBlocked: boolean;
  onPromote: PromoteHandler;
}

function RowBody({
  task,
  project,
  assigneeLabel,
  showProject,
  isBlocked,
}: Omit<RowProps, "onPromote">) {
  return (
    <>
      {showProject && project && (
        <div className="flex flex-shrink-0 items-center gap-1.5">
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full"
            style={{ background: project.color }}
          />
          <span className="hidden text-[11px] font-medium text-ink-muted sm:inline">{project.name}</span>
        </div>
      )}

      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
        {task.title}
      </span>

      <span className="hidden flex-shrink-0 truncate text-xs text-ink-muted sm:inline">
        {assigneeLabel}
      </span>

      {task.tags.length > 0 && (
        <div className="hidden flex-shrink-0 items-center gap-1 md:flex">
          {task.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-tag-bg px-2 py-0.5 text-[10px] font-semibold text-tag-text"
            >
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="text-[10px] text-ink-subtle">+{task.tags.length - 3}</span>
          )}
        </div>
      )}

      {isBlocked && (
        <span className="flex-shrink-0 rounded-full bg-danger-bg px-2 py-0.5 text-[10px] font-semibold text-danger-text">
          blocked
        </span>
      )}
    </>
  );
}

/**
 * Quick-promote buttons (Backlog → To Do / In Progress). Rendered as a sibling
 * of the row `<Link>`, never a descendant — a `<button>` inside an `<a>` is
 * invalid and would trigger anchor navigation. As a sibling it needs no
 * preventDefault/stopPropagation, and it sits outside the drag listeners so a
 * click never starts a reorder.
 */
function PromoteActions({ task, onPromote }: { task: Task; onPromote: PromoteHandler }) {
  return (
    <div className="hidden flex-shrink-0 items-center gap-1.5 pr-3 sm:flex">
      <button
        onClick={() => onPromote(task, "To Do")}
        className="rounded-md border border-border bg-surface-subtle px-2 py-1 text-[11px] font-semibold text-ink-muted transition-colors hover:border-border-focus hover:bg-surface-hover hover:text-ink"
      >
        → To Do
      </button>
      <button
        onClick={() => onPromote(task, "In Progress")}
        className="rounded-md border border-border bg-surface-subtle px-2 py-1 text-[11px] font-semibold text-ink-muted transition-colors hover:border-border-focus hover:bg-surface-hover hover:text-ink"
      >
        → In Progress
      </button>
    </div>
  );
}

function BacklogRow({
  task,
  project,
  assigneeLabel,
  showProject,
  isBlocked,
  onPromote,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id, data: { type: "task", taskId: task.id } });
  const isMobile = useIsMobile();

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  };

  const handleProps = isMobile ? { ...attributes, ...listeners } : {};
  const bodyProps = isMobile ? {} : { ...attributes, ...listeners };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        "flex overflow-hidden rounded-card bg-surface shadow-card",
        "transition-all duration-150 hover:shadow-card-hover",
        isBlocked && "border-l-[3px] border-danger",
      )}
    >
      <div
        {...handleProps}
        aria-label="Drag to reorder"
        className="flex w-7 flex-shrink-0 touch-none cursor-grab items-center justify-center bg-surface-subtle/60 text-ink-subtle active:cursor-grabbing sm:hidden"
      >
        <DragGripIcon />
      </div>
      <Link
        to={`/tasks/${task.id}`}
        draggable={false}
        {...bodyProps}
        className="flex flex-1 cursor-pointer items-center gap-3 px-3 py-2 sm:cursor-grab sm:active:cursor-grabbing"
      >
        <RowBody
          task={task}
          project={project}
          assigneeLabel={assigneeLabel}
          showProject={showProject}
          isBlocked={isBlocked}
        />
      </Link>
      <PromoteActions task={task} onPromote={onPromote} />
    </div>
  );
}

function BacklogRowOverlay({
  task,
  project,
  assigneeLabel,
  showProject,
  isBlocked,
  onPromote,
}: RowProps) {
  return (
    <div
      className={clsx(
        "flex cursor-grabbing overflow-hidden rounded-card bg-surface shadow-card shadow-card-hover",
        isBlocked && "border-l-[3px] border-danger",
      )}
    >
      <div className="flex w-7 flex-shrink-0 items-center justify-center bg-surface-subtle/60 text-ink-subtle sm:hidden">
        <DragGripIcon />
      </div>
      <div className="flex flex-1 items-center gap-3 px-3 py-2">
        <RowBody
          task={task}
          project={project}
          assigneeLabel={assigneeLabel}
          showProject={showProject}
          isBlocked={isBlocked}
        />
      </div>
      <PromoteActions task={task} onPromote={onPromote} />
    </div>
  );
}

function BacklogList({
  tasks,
  projectById,
  usersById,
  showProject,
  blockedIds,
  onPromote,
}: {
  tasks: Task[];
  projectById: Map<string, Project>;
  usersById: Map<string, User>;
  showProject: boolean;
  blockedIds: Set<string>;
  onPromote: PromoteHandler;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: "col:Backlog",
    data: { type: "column", column: "Backlog" },
  });

  return (
    <div
      ref={setNodeRef}
      style={
        isOver
          ? { outline: "2px solid var(--color-border-focus)", outlineOffset: "2px" }
          : undefined
      }
      className="min-h-[120px] rounded-xl p-1"
    >
      <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-1.5">
          {tasks.map((task) => (
            <BacklogRow
              key={task.id}
              task={task}
              project={task.project_id ? projectById.get(task.project_id) : undefined}
              assigneeLabel={formatAssigneeLabel(usersById, task.assigned_to)}
              showProject={showProject}
              isBlocked={blockedIds.has(task.id)}
              onPromote={onPromote}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

export function BacklogView() {
  const { activeSpaceId } = useActiveSpace();
  const { data: tasks = [], isLoading, isError, error } = useTasks(activeSpaceId);
  const { data: projects = [] } = useProjects(activeSpaceId);
  const { data: users = [] } = useUsers();
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const { selectedProject, selectedTags, selectedUsers } = useFilterContext();

  const moveMutation = useMoveTask();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const projectById = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects],
  );
  const usersById = useMemo<Map<string, User>>(() => createUserLookup(users), [users]);

  const backlogTasks = useMemo(
    () => tasks.filter((t) => t.column === "Backlog").sort((a, b) => a.position - b.position),
    [tasks],
  );

  const allTags = useMemo(() => collectTags(backlogTasks), [backlogTasks]);

  const filteredTasks = useMemo(
    () => applyTaskFilters(backlogTasks, { selectedProject, selectedTags, selectedUsers }),
    [backlogTasks, selectedProject, selectedTags, selectedUsers],
  );

  const blockedIds = useMemo(() => {
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    const ids = new Set<string>();
    for (const t of tasks) {
      if (isTaskBlocked(t, taskById)) ids.add(t.id);
    }
    return ids;
  }, [tasks]);

  function handlePromote(task: Task, targetColumn: Column) {
    const originalPosition = task.position;
    moveMutation.mutate(
      { id: task.id, version: task.version, column: targetColumn, position: 0 },
      {
        onSuccess: (updatedTask) => {
          toast.success(`Moved to ${targetColumn}`, {
            action: {
              label: "Undo",
              onClick: () => {
                moveMutation.mutate(
                  { id: task.id, version: updatedTask.version, column: "Backlog", position: originalPosition },
                  { onError: () => toast.error("Could not undo — task has changed") },
                );
              },
            },
          });
        },
      },
    );
  }

  function handleDragStart(ev: DragStartEvent) {
    const task = backlogTasks.find((t) => t.id === String(ev.active.id));
    setActiveTask(task ?? null);
  }

  function handleDragCancel() {
    setActiveTask(null);
  }

  function handleDragEnd(ev: DragEndEvent) {
    setActiveTask(null);
    const { active, over } = ev;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const draggedTask = backlogTasks.find((t) => t.id === activeId);
    if (!draggedTask) return;

    let targetIndex: number;
    if (overId === "col:Backlog") {
      targetIndex = backlogTasks.length;
    } else {
      targetIndex = backlogTasks.findIndex((t) => t.id === overId);
      if (targetIndex < 0) targetIndex = backlogTasks.length;
    }

    const reordered = backlogTasks.filter((t) => t.id !== activeId);
    const sourceIndex = backlogTasks.findIndex((t) => t.id === activeId);
    if (sourceIndex === targetIndex) return;

    const newPosition = computeInsertPosition(reordered, targetIndex);

    moveMutation.mutate({
      id: activeId,
      version: draggedTask.version,
      column: "Backlog",
      position: newPosition,
    });
  }

  if (isError) {
    return (
      <div className="p-6 text-sm text-danger">
        Failed to load tasks: {(error as Error).message}
      </div>
    );
  }
  if (isLoading) {
    return <div className="p-6 text-sm text-ink-muted">Loading…</div>;
  }

  return (
    <div className="flex h-full flex-col">
      <FilterBar allTags={allTags} />
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
          <div className="mx-auto max-w-4xl">
            <BacklogList
              tasks={filteredTasks}
              projectById={projectById}
              usersById={usersById}
              showProject={!selectedProject}
              blockedIds={blockedIds}
              onPromote={handlePromote}
            />
          </div>
        </div>
        <DragOverlay dropAnimation={null}>
          {activeTask ? (
            <BacklogRowOverlay
              task={activeTask}
              project={activeTask.project_id ? projectById.get(activeTask.project_id) : undefined}
              assigneeLabel={formatAssigneeLabel(usersById, activeTask.assigned_to)}
              showProject={!selectedProject}
              isBlocked={blockedIds.has(activeTask.id)}
              onPromote={handlePromote}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
